import { useDispatch, useSelector } from 'react-redux'
import {
  connectorActionModuleSelector,
  connectorLastFormActionTimeSelector,
  connectorLastFormActionTime,
} from '@redux/slices/connectorSlice'
import { unhandledError } from '@redux/slices/workflowSlice'
import { FORM_ACTION_INVOKE_THRESHOLD } from '@constants/index'
import { Logger } from '@utils/logger'
import { DateUtil } from '@utils/dateUtil'
import { useCallback } from 'react'

type UseInvokeActionReturn = (callback?: () => void) => void

/**
 * Invokes a callback then trigger the commerce onFormAction event to ensure session is alive.
 */
export const useInvokeAction = (): UseInvokeActionReturn => {
  const dispatch = useDispatch()
  const actionModule = useSelector(connectorActionModuleSelector)
  const lastTime = useSelector(connectorLastFormActionTimeSelector)

  const invokeAction = useCallback(
    (callback?: () => void) => {
      const isFormActionAvailable = typeof actionModule?.onFormAction === 'function'

      callback?.()

      if (!isFormActionAvailable) {
        return
      }

      // check time elapsed since last call and call onFormAction only if time has exceeded threshold
      const isWithinThreshold =
        lastTime && !DateUtil.isMaxTimeExceeded({ startTime: lastTime, maxTime: FORM_ACTION_INVOKE_THRESHOLD })

      if (isWithinThreshold) {
        return
      }

      // keep commerce session alive
      actionModule
        .onFormAction()
        .then(() => {
          dispatch(connectorLastFormActionTime())
        })
        .catch(e => {
          Logger.error('directbilling unable to invoke action', e)
          dispatch(unhandledError(e))
        })
    },
    [dispatch, actionModule, lastTime]
  )

  return invokeAction
}
